import React, { useState } from "react";
import {
  FieldErrors,
  useForm,
  UseFormClearErrors,
  UseFormSetValue,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { Box, Typography } from "@mui/material";

import { errorRed } from "design-system/colors";
import { workflowSchema, WorkflowSelectionsEnum } from "./schemaValidation";
import { workflowDefaultValues } from "./defaultValues";
import WorkflowOption from "./components/WorkflowOption";
import promoCodeAnytimeOptionSvg from "design-system/illustrations/promoCodeAnytimeOption.svg";
import promoCodeBookingDateOptionSvg from "design-system/illustrations/promoCodeBookingDateOption.svg";
import promoCodeTripDateOptionSvg from "design-system/illustrations/promoCodeTripDateOption.svg";
import { useScreenSize } from "globals/hooks";

type WorkflowValues = {
  workflow: string;
};

type WorkflowFormProps = {
  errors: FieldErrors;
  onSetValue: UseFormSetValue<WorkflowValues>;
  onClearErrors: UseFormClearErrors<WorkflowValues>;
  workflowOutline: WorkflowSelectionsEnum;
  onSetWorkflowOutline: (state) => void;
};

function WorkflowForm(props: WorkflowFormProps) {
  const {
    errors,
    onSetValue,
    onClearErrors,
    workflowOutline,
    onSetWorkflowOutline,
  } = props;

  const error = errors.workflow;

  // hooks
  const { isSmallTabletView } = useScreenSize();

  // event handler
  const handleClick = (e) => {
    const selectedWorkflowOutline = e.currentTarget.dataset.workflowKey;

    onSetWorkflowOutline(selectedWorkflowOutline);
    onClearErrors("workflow");
    onSetValue("workflow", selectedWorkflowOutline);
  };

  return (
    <Box display="flex" flexDirection="column">
      <Box
        display="flex"
        flexDirection={isSmallTabletView ? "column" : "row"}
        justifyContent="space-between"
        marginTop={isSmallTabletView ? "20px" : "0px"}
      >
        <WorkflowOption
          workflowKey={WorkflowSelectionsEnum.anytime}
          image={promoCodeAnytimeOptionSvg}
          header="Anytime"
          body={
            <Typography variant="body2">
              Booking contacts can use this promo code anytime
            </Typography>
          }
          onClick={handleClick}
          error={errors.workflow}
          workflowOutline={workflowOutline}
        />
        <WorkflowOption
          workflowKey={WorkflowSelectionsEnum.bookingDate}
          image={promoCodeBookingDateOptionSvg}
          header="Booking Date"
          body={
            <Typography variant="body2">
              Booking contacts can only use this promo code{" "}
              <strong>when booking date is within a date range</strong>
            </Typography>
          }
          onClick={handleClick}
          error={errors.workflow}
          workflowOutline={workflowOutline}
          tooltipText="Target low booking dates to increase reservations during that time."
        />
        <WorkflowOption
          workflowKey={WorkflowSelectionsEnum.tripDate}
          image={promoCodeTripDateOptionSvg}
          header="Trip Date"
          body={
            <Typography variant="body2">
              Booking contacts can only use this promo code{" "}
              <strong>when trip date is within a date range</strong>
            </Typography>
          }
          onClick={handleClick}
          error={errors.workflow}
          workflowOutline={workflowOutline}
          tooltipText="Target low trip dates where you have fewer trips occurring."
        />
      </Box>
      {error && (
        <Box mt={3}>
          <Typography style={{ color: errorRed }}>{error.message}</Typography>
        </Box>
      )}
    </Box>
  );
}

function useWorkflowForm() {
  // state
  // workflowOutline will persist when goes back to first step
  const [workflowOutline, setWorkflowOutline] =
    useState<WorkflowSelectionsEnum>(null);

  // setup react hook form
  const {
    setValue,
    clearErrors,
    formState: { errors },
    handleSubmit: handleWorkflowSubmit,
    reset: resetWorkflowForm,
  } = useForm({
    defaultValues: workflowDefaultValues,
    resolver: yupResolver(workflowSchema),
  });

  return {
    handleWorkflowSubmit,
    setWorkflowOutline,
    resetWorkflowForm,
    workFlowFormComponent: (
      <WorkflowForm
        errors={errors}
        onSetValue={setValue}
        onClearErrors={clearErrors}
        onSetWorkflowOutline={setWorkflowOutline}
        workflowOutline={workflowOutline}
      />
    ),
  };
}

export default useWorkflowForm;
